import React, { useState, useEffect } from 'react';
import MainLayout from '../layouts/MainLayout';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, InputAdornment,
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility, VisibilityOff, LockOutlined as LockIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import { credencialService, Credencial, CreateCredencialDTO, UpdateCredencialDTO } from '../services/credencialService';
import ConfirmationDialog from '../components/ConfirmationDialog';

export default function GerenciarCredenciaisPage() {
  const [credenciais, setCredenciais] = useState<Credencial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [credencialAtual, setCredencialAtual] = useState<Partial<Credencial> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credencialParaDeletar, setCredencialParaDeletar] = useState<number | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const carregarCredenciais = async () => {
    try {
      setLoading(true);
      const data = await credencialService.listar();
      setCredenciais(data);
    } catch (error: any) {
      toast.error('Erro ao carregar as credenciais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCredenciais();
  }, []);

  const handleOpenModal = (credencial?: Credencial) => {
    setCredencialAtual(credencial ? { ...credencial } : { url: '', usuario: '', senha: '', detalhes: '' });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCredencialAtual(null);
    setShowPassword(false); // Reseta a visibilidade da senha ao fechar
  };

  const handleSalvar = async () => {
    if (!credencialAtual) return;

    if (!credencialAtual.url || !credencialAtual.usuario || (!credencialAtual.id && !credencialAtual.senha)) {
      toast.warn('Preencha todos os campos obrigatórios (URL, Usuário e Senha para novas credenciais).');
      return;
    }

    try {
      if (credencialAtual.id) {
        // Atualizar
        const dadosParaAtualizar: UpdateCredencialDTO = {
          url: credencialAtual.url,
          usuario: credencialAtual.usuario,
          detalhes: credencialAtual.detalhes,
        };
        // Só envia a senha se ela for alterada
        if (credencialAtual.senha) {
          dadosParaAtualizar.senha = credencialAtual.senha;
        }
        await credencialService.atualizar(credencialAtual.id, dadosParaAtualizar);
        toast.success('Credencial atualizada com sucesso!');
      } else {
        // Criar
        await credencialService.criar(credencialAtual as CreateCredencialDTO);
        toast.success('Credencial criada com sucesso!');
      }
      handleCloseModal();
      carregarCredenciais();
    } catch (error: any) {
      toast.error(`Erro ao salvar a credencial.`);
    }
  };

  const handleOpenDeleteDialog = (id: number) => {
    setCredencialParaDeletar(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setCredencialParaDeletar(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!credencialParaDeletar) return;

    try {
      await credencialService.deletar(credencialParaDeletar);
      toast.success('Credencial deletada com sucesso!');
      handleCloseDeleteDialog();
      carregarCredenciais();
    } catch (error: any) {
      toast.error('Erro ao deletar a credencial.');
    }
  };

  const handleCopyPassword = () => {
    if (credencialAtual?.senha) {
      navigator.clipboard.writeText(credencialAtual.senha)
        .then(() => {
          toast.info('Senha copiada para a área de transferência!');
        })
        .catch(err => {
          toast.error('Falha ao copiar a senha.');
        });
    }
  };
  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{xs:12}}>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LockIcon  sx={{ fontSize: '2.2rem' }} />
                  <Typography component="h1" variant="h4" gutterBottom sx={{ mb: 0 }}>
                    Credenciais
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Adicionar Nova
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>URL/Sistema</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Complemento</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {credenciais.map((credencial) => (
                        <TableRow key={credencial.id} hover>
                          <TableCell>{credencial.url}</TableCell>
                          <TableCell>{credencial.detalhes}</TableCell>
                          <TableCell>{credencial.usuario}</TableCell>
                          <TableCell align="center">
                            <IconButton color="primary" onClick={() => handleOpenModal(credencial)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton color="error" onClick={() => handleOpenDeleteDialog(credencial.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Modal de Criar/Editar */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{credencialAtual?.id ? 'Editar Credencial' : 'Nova Credencial'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="URL / Sistema"
            type="text"
            fullWidth
            variant="outlined"
            value={credencialAtual?.url || ''}
            onChange={(e) => setCredencialAtual({ ...credencialAtual, url: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Usuário"
            type="text"
            fullWidth
            variant="outlined"
            value={credencialAtual?.usuario || ''}
            onChange={(e) => setCredencialAtual({ ...credencialAtual, usuario: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={credencialAtual?.senha || ''}
            onChange={(e) => setCredencialAtual({ ...credencialAtual, senha: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {credencialAtual?.id && credencialAtual?.senha && (
                    <IconButton
                      aria-label="copy password"
                      onClick={handleCopyPassword}
                    ><ContentCopyIcon /></IconButton>
                  )}
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Complemento"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={credencialAtual?.detalhes || ''}
            onChange={(e) => setCredencialAtual({ ...credencialAtual, detalhes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSalvar} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta credencial? Esta ação não pode ser desfeita."
      />

      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </MainLayout>
  );
}